import React from "react";
import { Link } from "react-router-dom";

import {
  LearnerIndexCoach,
  LearnerIndexScore,
  LearnerIndexScoreChange,
} from "apps/user/learner/api/types";

import IndexScore from "apps/user/common/components/index-score";

import IconIncrease from "Assets/icon-increase.svg";
import IconDecrease from "Assets/icon-decrease.svg";
import IconIncreaseBlue from "Assets/icon-increase-blue.svg";
import IconDecreaseBlue from "Assets/icon-decrease-blue.svg";
import IconNoChangeNeutral from "Assets/icon-no-change-neutral.svg";
import { ReactComponent as InformationIcon } from "Assets/interfaceEssentialInfoInformationCircle.svg";
import styles from "./../index.module.scss";
import { Button } from "components/ui";
import IconUpdatedBlackLock from "Assets/icon/icon_updated_black_lock.svg";
import { logAnalyticsEvent } from "utility/FirebaseAnalytics";
import { trackEvent } from "utility/mixpanel";
import { handleRetakeStartAssessment } from "../utils";
import { useTranslation } from "lib/i18n/use-translation";

interface IndexScoreChangeProps {
  change?: LearnerIndexScoreChange;
  className?: string;
  style?: React.CSSProperties;
  iconColor?: "red" | "blue";
}

export const IndexScoreChange = ({
  change,
  className,
  style,
  iconColor = "red",
}: IndexScoreChangeProps) => {
  if (!change) {
    return <div className={className} style={style}></div>;
  }

  let Icon;
  let iconAlt;
  if (change.direction === "UP") {
    Icon = iconColor === "red" ? IconIncrease : IconIncreaseBlue;
    iconAlt = "Arrow pointing right and up";
  } else if (change.direction === "DOWN") {
    Icon = iconColor === "red" ? IconDecrease : IconDecreaseBlue;
    iconAlt = "Arrow pointing left and down";
  } else {
    Icon = IconNoChangeNeutral;
    iconAlt = "Arrow pointing right";
  }

  return (
    <div className={className} style={style}>
      <img src={Icon} alt={iconAlt} />
      <div dangerouslySetInnerHTML={{ __html: change.textHtml }}></div>
    </div>
  );
};

interface IndexScoreContainerProps {
  to?: string;
  pillarLabel?: string;
  data?: LearnerIndexScore;
  hideLabel?: boolean;
  isTrackPage?: boolean;
  coach?: LearnerIndexCoach;
  className?: string;
  description?: string;
}

const IndexScoreContainer = ({
  to,
  pillarLabel,
  data,
  hideLabel,
  isTrackPage,
  coach,
}: IndexScoreContainerProps) => {
  const { t } = useTranslation("index");

  const handleAssessmentButtonClick = async (
    e: React.MouseEvent<HTMLElement>
  ) => {
    if (e.target instanceof HTMLButtonElement) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (!coach?.assessmentButton) {
      return;
    }

    trackEvent("Assessment CTA Track Clicked");
    logAnalyticsEvent("clicked_assessment_cta_in_track_screen", {});

    const url = new URL(coach.assessmentButton.url);

    handleRetakeStartAssessment(e, url);
  };

  const Container = ({ children }) => {
    if (data && to) {
      return (
        <Link to={to} className={styles["score-container"]}>
          {children}
          <img
            className={styles["info-icon"]}
            src={InformationIcon}
            alt="icon-info"
          />
        </Link>
      );
    } else {
      return <div className={styles["score-container"]}>{children}</div>;
    }
  };

  const indexValue = parseInt(data?.value);
  const indexScoreBandDescription = () => {
    if (indexValue >= 0 && indexValue <= 20) {
      return {
        scoreBand: t("index:container.scoreBand.rebuild.title"),
        scoreDescription: t("index:container.scoreBand.rebuild.description", {
          value: indexValue,
        }),
      };
    } else if (indexValue >= 21 && indexValue <= 40) {
      return {
        scoreBand: t("index:container.scoreBand.underPressure.title"),
        scoreDescription: t(
          "index:container.scoreBand.underPressure.description",
          { value: indexValue }
        ),
      };
    } else if (indexValue >= 41 && indexValue <= 60) {
      return {
        scoreBand: t("index:container.scoreBand.rebalance.title"),
        scoreDescription: t("index:container.scoreBand.rebalance.description", {
          value: indexValue,
        }),
      };
    } else if (indexValue >= 61 && indexValue <= 80) {
      return {
        scoreBand: t("index:container.scoreBand.balanced.title"),
        scoreDescription: t("index:container.scoreBand.balanced.description", {
          value: indexValue,
        }),
      };
    } else if (indexValue >= 81 && indexValue <= 100) {
      return {
        scoreBand: t("index:container.scoreBand.strong.title"),
        scoreDescription: t("index:container.scoreBand.strong.description", {
          value: indexValue,
        }),
      };
    }
  };

  let labelHtml = data ? data.labelHtml : t("index:container.yourWoneIndex");

  if (!data) {
    return (
      <div
        className={styles["no-index-score-home"]}
        onClick={handleAssessmentButtonClick}
      >
        <div className={styles["index-score-empty-state"]}>
          <div className={styles["index-score-title"]}>
            <p dangerouslySetInnerHTML={{ __html: labelHtml }}></p>
          </div>
          <p className={styles["no-index-description"]}>
            {t("index:container.emptyDescription")}
          </p>

          <img
            className={styles["lock-icon"]}
            src={IconUpdatedBlackLock}
            alt="empty state"
          />

          {coach?.assessmentButton && (
            <div className={styles["no-index-button"]}>
              <Button
                variant="enclosed-primary"
                onClick={handleAssessmentButtonClick}
                type="submit"
              >
                {t("index:container.unlockButton")}
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  }
  if (isTrackPage) {
    return (
      <div className={styles["index-score-content-track"]}>
        <div>
          <div className={styles["index-score-info"]}>
            <Link to={to}>
              <InformationIcon className={styles["info-icon"]} />
            </Link>
          </div>
          <div className={styles["index-score-details"]}>
            <IndexScore
              indexScoreData={data}
              size={"default"}
              showIcon={false}
            />
            <IndexScoreChange
              change={data?.change}
              className={styles["percent-container"]}
              iconColor={pillarLabel ? "blue" : "red"}
            />
          </div>
        </div>

        <div className={styles["index-score-bands"]}>
          <div className={styles["index-score-band-title"]}>
            <p>{indexScoreBandDescription().scoreBand}</p>
          </div>
          <p className={styles["index-score-band-description"]}>
            {indexScoreBandDescription().scoreDescription}
          </p>
        </div>
      </div>
    );
  }
  return (
    <div className={styles["index-score"]}>
      {!hideLabel && (
        <p
          className={styles["index-score-title"]}
          dangerouslySetInnerHTML={{ __html: labelHtml }}
        ></p>
      )}
      <Container>
        <IndexScore indexScoreData={data} size={"big"} showIcon={false} />
      </Container>
      <IndexScoreChange
        change={data?.change}
        className={styles["percent-container"]}
        iconColor={pillarLabel ? "blue" : "red"}
      />
    </div>
  );
};

export default IndexScoreContainer;
